import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import {
    ISmartField,
    TSmartFieldCategory,
    TSmartFields
} from '~/common/SmartFields/smart-fields.types';
import {
    CUSTOM_CLIENT_FIELD_KEY_PREFIX,
    CUSTOM_PROJECT_FIELD_KEY_PREFIX,
    CUSTOM_SECOND_CLIENT_FIELD_KEY_PREFIX,
    TSmartFieldKeys
} from '~/common/SmartFields/server.types';
import { SMART_FIELD_CLASSNAME } from '~/components/OneComposer/one-composer.consts';
import { SMART_FIELDS_MARKER } from '~/common/SmartFields/smart-fields.constants';
import { getFullUrl } from '~/utils/url.utils';
import { smartFieldTranslated } from '~/services/smartfield.service';
import { isPublicTemplateViewMode } from '~/utils/public-template-view-mode-flow.utils';
import {
    ILoggerServiceProps,
    LoggerService
} from '~/services/logger/logger.service';

export const smartfieldsLoggerProps: ILoggerServiceProps = {
    featureName: 'smartFields',
    messagePrefix: '[SmartFields]',
    featureVersion: 1
};

const loggerService = new LoggerService(smartfieldsLoggerProps);

const SERVER_SMART_FIELD_REGEX = /\{field:[^}]+\}/; // matches "{field:key}"

const SERVER_SMART_FIELD_KEY_REGEX = /\{field:([^}]+)\}/g; // matches "{field:key}" and yields `key`

const VIEW_SMART_FIELD_REGEX = // matches "<span data-field-key="key">text</span>" and yields `key`
    /<span\s+[^>]*data-field-key="([^"]*)"[^>]*>([\D\d]*?)<\/span>/g;

const LEGACY_SMART_FIELDS_KEYS_REGEX = // matches "owner.full_name" or "client.first.first_name", case insensitive
    /OWNER\.FULL_NAME|CLIENT\.FIRST\.FIRST_NAME/i;

const SERVER_LEGACY_CLIENT_FIRST_NAME_REGEX = // matches "{field:CLIENT.FIRST.FIRST_NAME}" or "<input***>, case insensitive
    /{field:CLIENT\.FIRST\.FIRST_NAME}|<input[^>]*>/gi;

const SERVER_LEGACY_OWNER_FULL_NAME_REGEX = /{field:OWNER\.FULL_NAME}/gi; // matches "{field:OWNER.FULL_NAME}", case insensitive

export function getSmartFieldIdentifier({
    linked_id,
    source,
    key
}: ISmartField): string {
    return linked_id ? `${source}.${linked_id}` : key;
}

export const getItemCategory = (
    source: ISmartField['source']
): TSmartFieldCategory => {
    switch (source) {
        case CUSTOM_PROJECT_FIELD_KEY_PREFIX:
            return 'project';
        case CUSTOM_CLIENT_FIELD_KEY_PREFIX:
            return 'client';
        case CUSTOM_SECOND_CLIENT_FIELD_KEY_PREFIX:
            return 'second_client';
        default:
            return source;
    }
};

export function hasServerFormatSmartFields(content: string) {
    return SERVER_SMART_FIELD_REGEX.test(content);
}

export function injectSmartFieldValues({
    content,
    smartFields
}: {
    content: string;
    smartFields: TSmartFields;
}) {
    let output = convertSmartFieldsToServerFormat(content);

    output = output.replace(
        SERVER_SMART_FIELD_KEY_REGEX,
        (match, key: TSmartFieldKeys) => {
            const smartField =
                smartFields[key] ||
                Object.values(smartFields).find(
                    sf => sf.key == key || getSmartFieldIdentifier(sf) == key
                );
            if (!smartField) {
                loggerService.trackError({
                    error: new Error(
                        `@injectSmartFieldsValues: couldn't find smartfield of key "${key}"`
                    ),
                    context: { smartFields, key }
                });
                return '';
            }

            const sanitizedSmartFieldValue = smartField.is_available
                ? DOMPurify.sanitize(smartField.value)
                : isPublicTemplateViewMode()
                ? GenerateSmartFieldPublicTemplateViewSpan(key)
                : '';

            if (key.includes('email')) {
                return smartField.is_available
                    ? convertEmailToEmailLink(sanitizedSmartFieldValue)
                    : '';
            }

            if (key.includes('url')) {
                return smartField.is_available
                    ? convertUrlToUrlLink(sanitizedSmartFieldValue)
                    : '';
            }

            return sanitizedSmartFieldValue;
        }
    );

    return output;
}

export function getUsedSmartFieldKeys(content: string): Array<TSmartFieldKeys> {
    const serverFormatContent = convertSmartFieldsToServerFormat(content);
    const smartFieldKeys = new Set<TSmartFieldKeys>();
    serverFormatContent.replace(
        SERVER_SMART_FIELD_KEY_REGEX,
        (match, key: TSmartFieldKeys) => {
            smartFieldKeys.add(key);
            return '';
        }
    );
    return Array.from(smartFieldKeys);
}

export function getAreSomeSmartFieldsInvalid(content: string) {
    let parsedHtml: Document;
    try {
        parsedHtml = new DOMParser().parseFromString(content, 'text/html');
    } catch (error) {
        loggerService.trackError({
            error,
            message: '@smart-fields.utils.ts: content is not valid HTML',
            context: { content }
        });
        throw error;
    }
    const smartFields = parsedHtml.querySelectorAll(
        `.${SMART_FIELD_CLASSNAME}`
    );

    return Array.from(smartFields).some(
        smartField => smartField.getAttribute('data-is-valid') !== 'true'
    );
}

export function convertSmartFieldsToServerFormat(content: string) {
    if (!content) {
        return '';
    }
    return content.replace(
        VIEW_SMART_FIELD_REGEX,
        (match, key: TSmartFieldKeys) => {
            return `{field:${key}}`;
        }
    );
}

function generateSmartFieldViewSpan({
    smartField
}: {
    smartField: ISmartField;
}) {
    const { is_available, is_valid, text } = smartField;
    const smartFieldIdentifier = getSmartFieldIdentifier(smartField);
    const sanitizedText = DOMPurify.sanitize(text);
    return `<span class="${SMART_FIELD_CLASSNAME}" data-field="${SMART_FIELDS_MARKER}${smartFieldIdentifier}" data-field-key="${smartFieldIdentifier}" data-is-available="${is_available}" data-is-valid="${is_valid}">${sanitizedText}</span>`;
}

export function convertServerSmartFieldsToEditorFormat({
    content,
    smartFields
}: {
    content: string;
    smartFields: TSmartFields;
}) {
    if (!content) {
        return '';
    }

    let output = content.replace(
        SERVER_SMART_FIELD_KEY_REGEX,
        (match, key: TSmartFieldKeys) => {
            const smartField =
                smartFields[key] ||
                Object.values(smartFields).find(
                    sf => sf.key == key || getSmartFieldIdentifier(sf) == key
                );

            if (smartField) {
                return generateSmartFieldViewSpan({
                    smartField
                });
            }

            const isLegacySmartField = LEGACY_SMART_FIELDS_KEYS_REGEX.test(key);
            if (isLegacySmartField) {
                return match;
            }

            loggerService.trackError({
                message:
                    '@smart-fields.utils.ts:convertSmartFieldsToEditorFormat: Smart field not found',
                error: new Error(
                    `@smart-fields.utils.ts: Smart field: "${key}" not found`
                ),
                context: { content, smartFields }
            });

            return '';
        }
    );

    // backward compatibility for previous smart fields (first.client.first.name & owner.full.name) representations:
    const clientFirstNameSmartField = smartFields?.['client.first_name'];
    if (clientFirstNameSmartField) {
        output = output.replace(SERVER_LEGACY_CLIENT_FIRST_NAME_REGEX, () => {
            return generateSmartFieldViewSpan({
                smartField: clientFirstNameSmartField
            });
        });
    }

    const ownerFullNameSmartField = smartFields?.['user.full_name'];
    if (ownerFullNameSmartField) {
        output = output.replace(SERVER_LEGACY_OWNER_FULL_NAME_REGEX, () => {
            return generateSmartFieldViewSpan({
                smartField: ownerFullNameSmartField
            });
        });
    }

    return output;
}

const convertEmailToEmailLink = (email: string) => {
    return `<a href="mailto:${email}">${email}</a>`;
};
const convertUrlToUrlLink = (url: string) => {
    return `<a href="${getFullUrl(url)}" target="_blank">${url}</a>`;
};

function GenerateSmartFieldPublicTemplateViewSpan(key: TSmartFieldKeys) {
    const { t: smartFieldT } = useTranslation(NAMESPACES.SMART_FIELDS, {
        keyPrefix: 'smart_fields'
    });
    const label = smartFieldTranslated(key, smartFieldT) as string;
    return `<span class="${SMART_FIELD_CLASSNAME}">${label}</span>`;
}
